import React from "react";
import { Html, KeyboardControls, PerspectiveCamera } from "@react-three/drei";
import { Canvas, useThree } from "@react-three/fiber";
import { useCallback, useEffect, useRef, useState } from "react";
import { Vector3 } from "three";
import { CuboidTag } from "../components/CuboidTag";
import { PointCloud } from "../components/PointCloud";
import {
  keyboardControlsMapping,
  SceneControls,
} from "../components/SceneControls";
import type { Dataset } from "../types";

const minimumFrames = 10;
const frameCount = 50;

function useProgressiveFrameLoader(): {
  frames: (Dataset | undefined)[];
  loading: boolean;
  error: Error | null;
} {
  const [frames, setFrames] = useState<(Dataset | undefined)[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    const temp: (Dataset | undefined)[] = Array(frameCount).fill(undefined);
    let ready = 0;

    const loadFrame = async (i: number) => {
      const url = `data/frames/frame_${String(i).padStart(2, "0")}.json`;
      try {
        // Frames are vendored under static/data/frames so the demo runs
        // offline, without depending on Scale AI's external domain.
        // Original source (kept as reference / fallback):
        // `https://static.scale.com/uploads/pandaset-challenge/frame_${String(i).padStart(2, "0")}.json`
        const res = await fetch(url);
        if (!res.ok) {
          throw new Error(`Failed to fetch ${url}: HTTP ${res.status}`);
        }

        // Defensive parse: if the host serves something that is not valid
        // JSON (e.g. a Git LFS pointer when the data was committed via LFS
        // but never pulled), fail with a clear message instead of letting a
        // raw SyntaxError bubble up and crash the React tree.
        const raw = await res.text();
        let json: Dataset;
        try {
          json = JSON.parse(raw);
        } catch {
          throw new Error(
            `Frame ${url} is not valid JSON. ` +
              `Got: "${raw.slice(0, 60).replace(/\n/g, " ")}…". ` +
              `If you see a Git LFS pointer, the data was not materialized; ` +
              `the frames must be served as real JSON.`,
          );
        }

        temp[i] = json;
        ready++;

        if (ready === minimumFrames) {
          setFrames([...temp]);
          setLoading(false);
        } else if (ready > minimumFrames) {
          setFrames([...temp]);
        }
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Unknown error");
        // Surface a clear, un-minified message in the console so the failure
        // is debuggable in production builds too.
        console.error(`[frame-loader] ${error.message}`, error);
        setError(error);
      }
    };

    for (let i = 0; i < frameCount; i++) {
      loadFrame(i);
    }
  }, []);

  return { frames, loading, error };
}

function LoadingState() {
  const { camera } = useThree();
  const position = new Vector3(
    camera.position.x,
    camera.position.y,
    camera.position.z,
  );
  return (
    <Html position={position}>
      <div className="loading">Loading...</div>
    </Html>
  );
}

const FRAME_INTERVAL_MS = 100;

export default function Home() {
  const { frames, loading, error } = useProgressiveFrameLoader();
  const [playing, setPlaying] = useState(false);
  const [currentFrame, setCurrentFrame] = useState(0);
  const requestRef = useRef<number | undefined>(undefined);
  const lastTimestampRef = useRef<number | undefined>(undefined);
  const containerRef = useRef<HTMLCanvasElement>(null);

  const advanceFrame = () => {
    setCurrentFrame((prev) => (prev + 1) % frameCount);
  };

  const animate = (timestamp: number) => {
    if (!lastTimestampRef.current) lastTimestampRef.current = timestamp;
    const delta = timestamp - lastTimestampRef.current;

    if (delta >= FRAME_INTERVAL_MS) {
      advanceFrame();
      lastTimestampRef.current = timestamp;
    }

    requestRef.current = requestAnimationFrame(animate);
  };

  useEffect(() => {
    if (playing) {
      requestRef.current = requestAnimationFrame(animate);
    } else {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
      requestRef.current = undefined;
      lastTimestampRef.current = undefined;
    }

    return () => {
      if (requestRef.current) cancelAnimationFrame(requestRef.current);
    };
  }, [playing]);

  const onChangeCurrentFrame = useCallback(
    (frame: number) => {
      setCurrentFrame(frame);
    },
    [setCurrentFrame],
  );

  const onPlay = useCallback(() => setPlaying(true), [setPlaying]);
  const onStop = useCallback(() => setPlaying(false), [setPlaying]);
  const togglePlay = useCallback(
    () => setPlaying((prev) => !prev),
    [setPlaying],
  );

  // NOTE: every hook above must run on every render. Returning early *before*
  // these `useCallback`s (as this code used to) changes the number of hooks
  // between renders the moment `error` becomes truthy, which throws React
  // error #300 ("Rendered fewer hooks than expected") and tears down the
  // WebGL canvas ("THREE.WebGLRenderer: Context Lost"). Keep all early
  // returns below the last hook.
  if (error) {
    return (
      <main className="container">
        <div className="loading">Error loading frames: {error.message}</div>
      </main>
    );
  }

  const showLoading = loading || !frames[currentFrame];

  return (
    <main className="container" ref={containerRef}>
      <KeyboardControls map={keyboardControlsMapping}>
        <Canvas>
          <PerspectiveCamera
            makeDefault
            position={[0, -37, 30]}
            fov={75}
            far={150}
            rotation={[1.26, 0, 0]}
          />
          <SceneControls
            onChange={onChangeCurrentFrame}
            currentFrame={currentFrame}
            frameCount={frameCount}
            onPlay={onPlay}
            onStop={onStop}
            togglePlay={togglePlay}
            playing={playing}
            containerRef={containerRef}
            loading={loading}
          />
          <ambientLight intensity={0.3} />
          <pointLight position={[10, 10, 10]} />
          {showLoading && <LoadingState />}
          {frames[currentFrame]?.points && (
            <PointCloud points={frames[currentFrame].points} />
          )}
          {frames[currentFrame]?.cuboids.map((cuboid) => (
            <CuboidTag key={cuboid.uuid} cuboid={cuboid} />
          ))}
        </Canvas>
      </KeyboardControls>
    </main>
  );
}
